<template lang="html">
  <div class="catalog-categories overflow-hidden px-4">
    <ClientOnly>
      <SwiperContainer
        class="catalog-categories__slider"
        :modules="modules"
        :space-between="16"
        slidesPerView="auto"
        center-insufficient-slides
        :pagination="{
          clickable: true,
          dynamicBullets: true,
          type: 'bullets',
        }"
        @swiper="handleSwiperInited"
      >
        <SwiperSlide
          v-for="category in categories"
          :key="category.id"
          class="w-fit! max-w-60!"
        >
          <NuxtLink
            v-if="linkMode"
            class="group flex w-full flex-col items-center gap-2"
            :to="createLink(category)"
          >
            <div v-if="showCover" class="catalog-category__cover h-30 w-30">
              <UiPicture :src="category.images[0]" variant="imageFull" />
            </div>

            <div
              class="catalog-category__name w-full min-w-0 truncate group-hover:underline group-[.router-link-active]:underline"
            >
              {{ category.name }}
            </div>
          </NuxtLink>

          <button
            v-else
            type="button"
            class="group flex w-full cursor-pointer flex-col items-center gap-2"
            @click="selectCategory(category)"
          >
            <div v-if="showCover" class="catalog-category__cover h-30 w-30">
              <UiPicture :src="category.images[0]" variant="imageFull" />
            </div>

            <div
              class="catalog-category__name w-full min-w-0 truncate group-hover:underline"
              :class="{ underline: isSelected(category) }"
            >
              {{ category.name }}
            </div>
          </button>
        </SwiperSlide>
      </SwiperContainer>

      <template #fallback>
        <div class="flex justify-center gap-4 pb-3">
          <template v-for="category in categories" :key="category.id">
            <NuxtLink
              v-if="linkMode"
              class="group flex max-w-60! flex-col items-center gap-2"
              :to="createLink(category)"
            >
              <div v-if="showCover" class="catalog-category__cover h-30 w-30">
                <UiPicture :src="category.images[0]" variant="imageFull" />
              </div>

              <div
                class="catalog-category__name w-full min-w-0 truncate group-hover:underline"
              >
                {{ category.name }}
              </div>
            </NuxtLink>

            <button
              v-else
              type="button"
              class="group flex max-w-60! flex-col items-center gap-2"
              @click="selectCategory(category)"
            >
              <div v-if="showCover" class="catalog-category__cover h-30 w-30">
                <UiPicture :src="category.images[0]" variant="imageFull" />
              </div>

              <div
                class="catalog-category__name w-full min-w-0 truncate group-hover:underline"
                :class="{ underline: isSelected(category) }"
              >
                {{ category.name }}
              </div>
            </button>
          </template>
        </div>
      </template>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import type { EntityRef } from '@repo/api/web/models'
import 'swiper/swiper-bundle.css'
import type Swiper from 'swiper'
import { Pagination } from 'swiper/modules'
import { Swiper as SwiperContainer, SwiperSlide } from 'swiper/vue'

import { CategoryDto } from '#Products/models/Pim/CategoryDto'
import UiPicture from '~/app/components/Image/UiPicture.vue'

const props = withDefaults(
  defineProps<{
    getLink?: (category: CategoryDto) => any
    linkMode?: boolean
    selectedCategory?: CategoryDto
    variant?: 'short' | 'full'
  }>(),
  {
    linkMode: true,
    variant: 'full',
  },
)

const showCover = computed(() => props.variant === 'full')

const emit = defineEmits<{
  'update:selectedCategory': [category: CategoryDto | undefined]
}>()

const { routing } = useProducts()
const { allRoot: allCategories } = await useCategories()
const $slider = ref<Swiper>()

const modules = [Pagination]

const categories = computed(() =>
  allCategories.value?.toSorted((a, b) => a.order - b.order),
)

function createLink(category: CategoryDto) {
  if (props.getLink) return props.getLink(category)

  return routing.getCatalog({
    id: category.id,
    displayName: category.name,
  } as EntityRef).route
}

function isSelected(category: CategoryDto) {
  return props.selectedCategory?.id === category.id
}

function selectCategory(category: CategoryDto) {
  emit('update:selectedCategory', isSelected(category) ? undefined : category)
}

function scrollToSelected() {
  const idx = categories.value?.findIndex(
    c => c.id === props.selectedCategory?.id,
  )
  if (idx != null && idx >= 0) $slider.value?.slideTo(idx, 0)
}

function handleSwiperInited(swiper: any) {
  $slider.value = swiper
  scrollToSelected()
}

watch(() => props.selectedCategory?.id, () => {
  scrollToSelected()
})
</script>

<style lang="css">
.catalog-categories__slider {
  --swiper-pagination-bottom: 0px;
  --swiper-theme-color: var(--color-tertiary-500);

  padding-bottom: 24px !important;
}
</style>
